/**
 * System-agnostic engine — generic evaluator (v0.5, provisional).
 *
 * Pure TypeScript, no Vue, never imports `src/data/*`. Turns a `ContentPack` (rules-as-data) into
 * an evaluator that reproduces derived stats from declared expressions and resolves Layer C option
 * sets. The DSL is `expr-eval`, sandboxed with member access disabled so config authored by third
 * parties cannot reach JS internals.
 *
 * Governing principle: open *values* (which sets/options/predicates), closed *types* (the engine
 * knows a fixed, versioned vocabulary of source kinds and resolver operations — nothing it does not
 * recognise is silently honoured). See `docs/ENGINE_SPEC.md`.
 */
import { Parser, type Expression } from 'expr-eval'
import {
  parseSource,
  type ChoicePoint,
  type ContentPack,
  type OptionProvider,
  type ParsedSource,
  type ResolvedOption,
  type Scope,
} from './metamodel/types'
import { validatePack } from './validatePack'

/**
 * The `manifest.schemaVersion` this engine build understands. A newer *major* throws (loud-fail).
 * 1.1.0 (ADR-026 Option B): optional `scopeContract` pack field + `validatePack` cross-check.
 * 1.2.0 (ADR-030, INC-045): optional Layer D `play` key (turn/combat rules as data; `initiative`
 * axis lands first). Additive and optional minor bump — the major-only gate below is untouched, so
 * packs still declaring 1.1.0 (no `play`) keep working.
 * 1.3.0 (ADR-032, INC-046): optional `play.conditions` (declared expiry/lifecycle vocabulary; the four
 * D&D expiries become data). Additive/optional minor bump — same major-only gate, older packs unaffected.
 * 1.4.0 (ADR-031, INC-047): optional `play.dice` + `play.resolution` (closed dice-family set + declared
 * resolution ladder; the `total >= defense` hit rule becomes data). Additive/optional minor bump — same
 * major-only gate. Adding a *new dice family* later would be the major bump, not this key.
 * 1.5.0 (ADR-030, INC-049): optional `play.actions` (action-economy budget + downgrade edges) + `play.clocks`
 * (minimal progress clocks). **Declare-only** — no runtime consumer yet (B5); `SESSION_STATE_VERSION` is
 * untouched, no migration. Additive/optional minor bump — same major-only gate. Action-kind ids and clock
 * ids are open *config* (not a closed vocabulary), so declaring more of them is never a version bump.
 * 1.6.0 (ADR-086, INC-165): optional `play.recovery` (pack-declared recovery actions; closed effect kinds and
 * `actors` scope, open ids / usage keys / status ids checked by the host). Additive/optional minor bump — same
 * major-only gate; `SESSION_STATE_VERSION` untouched. Replaces the host's 4e `REST_RESETS`.
 */
export const SUPPORTED_SCHEMA_VERSION = '1.6.0'

export interface Engine {
  /** Evaluate a named derived stat from `pack.derived` against the given scope. */
  resolveDerived(id: string, scope: Scope): number
  /** Evaluate an ad-hoc parametric formula string (e.g. `pack.formulas.skillBonus`). */
  evaluate(expr: string, scope: Scope): number
  /** Layer C — evaluate a creation-flow step's `valid` predicate (host supplies scalar scope). */
  validateStep(stepId: string, scope: Scope): boolean
  /**
   * Layer C — resolve a choice point's option set. The engine composes: it asks the host-supplied
   * `provider` for the raw candidates (the only path `src/data/*` enters), then evaluates the
   * optional `eligible` predicate per candidate and tags each option `selectable`/`disabled`.
   */
  resolveOptions(
    choice: ChoicePoint | string,
    scope: Scope,
    provider: OptionProvider,
  ): ResolvedOption[]
  /**
   * Layer D §2.1 (ADR-031) — resolve a declared `play.resolution` rule to an outcome id: walk its
   * ordered `outcomes` ladder and return the first whose `when` predicate is truthy against `scope`.
   * The host injects the scalars the rule's `scope` names (e.g. `{ total, target }` for a d20 attack).
   * Throws on an unknown rule id, or when no outcome matches (closed == implemented: a rule must cover
   * its scope — a silent "no outcome" is the worst failure for a rules-as-data engine).
   */
  resolveOutcome(resolutionId: string, scope: Scope): string
}

export interface CreateEngineOptions {
  /** Run Zod runtime validation of the pack. Defaults to on (cheap; closed types make it so). */
  validate?: boolean
}

/** Parse `"x.y.z"` major component; non-numeric ⇒ 0. */
function majorOf(version: string): number {
  return Number.parseInt(version.split('.')[0] ?? '', 10) || 0
}

export function createEngine(pack: ContentPack, options: CreateEngineOptions = {}): Engine {
  // Loud-fail: a pack authored against a newer schema major declares operations this engine may
  // not implement — refuse rather than silently mis-resolve. (Same major / older ⇒ supported.)
  if (majorOf(pack.manifest.schemaVersion) > majorOf(SUPPORTED_SCHEMA_VERSION)) {
    throw new Error(
      `[engine] pack schemaVersion ${pack.manifest.schemaVersion} is newer than supported ${SUPPORTED_SCHEMA_VERSION}`,
    )
  }

  if (options.validate ?? true) validatePack(pack)

  // Member access OFF: expressions see only the flat scalar scope we hand them,
  // never JS objects/internals — safe for config authored by third parties.
  const parser = new Parser({ allowMemberAccess: false })

  // Register declared helper functions (mod, prof, …) as callable functions.
  for (const [name, fn] of Object.entries(pack.functions)) {
    const compiled = parser.parse(fn.expr)
    parser.functions[name] = (...args: Array<number | string>): number => {
      const local: Scope = {}
      fn.params.forEach((p, i) => (local[p] = args[i]!)) // params and args align by call contract
      return compiled.evaluate(local) as number
    }
  }

  const cache = new Map<string, Expression>()
  const compile = (expr: string): Expression => {
    let c = cache.get(expr)
    if (!c) {
      c = parser.parse(expr)
      cache.set(expr, c)
    }
    return c
  }

  const evaluate = (expr: string, scope: Scope): number => compile(expr).evaluate(scope) as number

  const resolveDerived = (id: string, scope: Scope): number => {
    const rule = pack.derived[id]
    if (!rule) throw new Error(`[engine] unknown derived stat: ${id}`)
    return evaluate(rule.expr, scope)
  }

  // Layer C — index the declared flow steps + choice points; parse every `source` once at load.
  // Parsing here (not lazily) means an unrecognised source kind fails the moment the engine is
  // created, not deep inside a render. See `parseSource` (loud-fail).
  const steps = new Map((pack.flow?.steps ?? []).map((s) => [s.id, s]))
  const choices = new Map<string, ChoicePoint>()
  const parsedSources = new Map<string, ParsedSource>()
  for (const step of pack.flow?.steps ?? []) {
    for (const choice of step.choices) {
      choices.set(choice.id, choice)
      parsedSources.set(choice.id, parseSource(choice.source))
    }
  }

  const validateStep = (stepId: string, scope: Scope): boolean => {
    const step = steps.get(stepId)
    if (!step) throw new Error(`[engine] unknown flow step: ${stepId}`)
    // Comparisons yield booleans, but a bare-variable predicate (e.g. `originSelected`)
    // yields the raw 1|0 scalar — coerce so the contract is always a boolean.
    return Boolean(compile(step.valid).evaluate(scope))
  }

  const resolveOptions = (
    choice: ChoicePoint | string,
    scope: Scope,
    provider: OptionProvider,
  ): ResolvedOption[] => {
    const cp = typeof choice === 'string' ? choices.get(choice) : choice
    if (!cp) throw new Error(`[engine] unknown choice point: ${String(choice)}`)
    // Reuse the load-time parse for declared choices; parse ad-hoc ones on the spot.
    const parsed = parsedSources.get(cp.id) ?? parseSource(cp.source)
    const raw = provider(parsed, scope, cp.id)

    const eligible = cp.eligible ? compile(cp.eligible) : null
    return raw.map((opt) => {
      let selectable = true
      if (eligible) {
        // Per-candidate scope: base scalars + the candidate's own scalar fields (booleans → 1|0)
        // + `candidateId`. The host owns the data; the engine owns the predicate evaluation.
        const candidateScope: Scope = { ...scope, candidateId: opt.id }
        for (const [key, value] of Object.entries(opt)) {
          if (key === 'id' || key === 'label' || value === undefined) continue
          candidateScope[key] = typeof value === 'boolean' ? (value ? 1 : 0) : value
        }
        selectable = Boolean(eligible.evaluate(candidateScope))
      }
      return { ...opt, selectable, disabled: !selectable }
    })
  }

  // Layer D §2.1 — walk a declared resolution rule's outcome ladder (first truthy `when` wins). Reuses
  // the same compiled-expr evaluator as derived stats (the Layer B evaluator), so a rule's predicates
  // read the flat scalar scope the host injects; unknown rule / no match ⇒ loud-fail (ADR-031).
  const resolveOutcome = (resolutionId: string, scope: Scope): string => {
    const rule = pack.play?.resolution?.[resolutionId]
    if (!rule) throw new Error(`[engine] unknown resolution rule: ${resolutionId}`)
    for (const outcome of rule.outcomes) {
      if (compile(outcome.when).evaluate(scope)) return outcome.id
    }
    throw new Error(
      `[engine] resolution rule "${resolutionId}" matched no outcome for scope ${JSON.stringify(scope)}`,
    )
  }

  return { resolveDerived, evaluate, validateStep, resolveOptions, resolveOutcome }
}
